import { DateTime } from "luxon";
import type { TaskWithFlags } from "@desk/contracts";
import type { TaskGroup } from "@/concepts/types";
import { addDays } from "./dates";
import { dedupe } from "./filters";

/**
 * Things-style Upcoming: each of the next 7 days gets its own header (big day number + weekday),
 * even when empty; the rest of this month collapses into a range header, then whole months, which
 * appear only when they hold something.
 */
export function buildUpcomingGroups(
  days: { date: string; planned: TaskWithFlags[]; deadlines: TaskWithFlags[] }[],
  today: string,
): TaskGroup[] {
  const byDate = new Map(days.map((d) => [d.date, dedupe([...d.deadlines, ...d.planned]).filter((t) => t.status !== "done" && t.status !== "cancelled")]));
  const groups: TaskGroup[] = [];
  const first = addDays(today, 1);
  for (let i = 0; i < 7; i++) {
    const date = addDays(first, i);
    const dt = DateTime.fromISO(date, { zone: "UTC" });
    groups.push({
      key: date,
      variant: "day",
      dayNumber: String(dt.day),
      label: i === 0 ? "Tomorrow" : dt.toFormat("cccc"),
      tasks: byDate.get(date) ?? [],
      showWhenEmpty: true,
    });
  }
  const afterWeek = addDays(first, 7);
  const lastFetched = days.length ? days[days.length - 1]!.date : afterWeek;
  const startDt = DateTime.fromISO(afterWeek, { zone: "UTC" });
  const monthEnd = startDt.endOf("month").toISODate()!;
  const collect = (from: string, to: string) => {
    const out: TaskWithFlags[] = [];
    for (const [d, ts] of byDate) if (d >= from && d <= to) out.push(...ts);
    return dedupe(out.sort((x, y) => (x.deadline ?? x.plannedDate ?? "").localeCompare(y.deadline ?? y.plannedDate ?? "")));
  };
  if (startDt.day !== 1) {
    groups.push({
      key: `range-${afterWeek}`,
      variant: "range",
      label: `${startDt.toFormat("LLL d")}–${DateTime.fromISO(monthEnd, { zone: "UTC" }).toFormat("d")}`,
      tasks: collect(afterWeek, monthEnd),
      showWhenEmpty: true,
    });
  }
  let cursor = startDt.day === 1 ? startDt : startDt.plus({ months: 1 }).startOf("month");
  while (cursor.toISODate()! <= lastFetched) {
    const from = cursor.toISODate()!;
    const to = cursor.endOf("month").toISODate()!;
    groups.push({
      key: `month-${from}`,
      variant: "month",
      label: cursor.year === DateTime.fromISO(today, { zone: "UTC" }).year ? cursor.toFormat("LLLL") : cursor.toFormat("LLLL yyyy"),
      tasks: collect(from, to),
    });
    cursor = cursor.plus({ months: 1 });
  }
  return groups;
}

